<template>
  <div>
    <div class="filters-wrapper">
      <div class="form-group">
        <input
          type="text"
          class="form-control full-width-input"
          v-model="search"
          @input="onHandleSearch"
        />
      </div>

      <button
        class="btn btn-md btn-secondary"
        @click="onClearAgendaFilters"
        :disabled="isFetching"
      ><i class="fa-regular fa-filter-slash"></i> Clear All Filters</button>

      <button
        class="btn btn-md btn-primary"
        @click="onOpenAgendaFiltersModal"
        :disabled="isFetching"
      ><i class="fa-regular fa-filter"></i> Filter</button>
    </div>

    <div class="d-flex justify-content-center" v-if="isFetching">
      <event-spinner />
    </div>

    <div
      class="flex-table default-shadow"
      v-show="agendaSessions?.length && !isFetching"
    >
      <template v-for="agendaSession in agendaSessions">
        <!-- group by agenda day -->
        <div class="agenda-day">{{ agendaSession.day_short }}</div>

        <!-- sessions table grouped by day -->
        <table
          v-if="agendaSession?.times?.length"
          class="table table-bordered"
          v-for="(sessionGroup, sessionGroupIndex) in agendaSession.times"
          :key="`day-${sessionGroupIndex}-${agendaSession.day}`"
        >
          <thead>
            <tr>
              <th class="table-header">
                <div class="time-wrapper">
                  <time-icon
                    :organization-id="organizationId"
                  />

                  <!-- session period -->
                  <div class="time">{{ sessionGroup.period }}</div>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-if="agendaSession?.times?.length"
              v-for="session in sessionGroup.items"
              :key="`session-${session.id}`"
            >
              <td>
                <div :class="{ 'sponsor-title-flex': session.sponsor }">
                  <div>
                    <div class="flex-table__session-title">
                      <!-- agenda name -->
                      <span
                        style="color: #000000"
                        v-if="session.is_agenda && session.name"
                        >{{ session.name }}</span
                      >

                      <!-- session name -->
                      <a
                        href="#"
                        v-if="!session.is_agenda && session.name"
                        @click.prevent="onOpenSessionDetailModal(session)"
                        :data-id="session.id"
                        >{{
                          session.code
                            ? `${session.code} - ${session.name}`
                            : session.name
                        }}</a
                      >
                    </div>

                    <!-- session description -->
                    <div
                      v-if="
                        (session.is_agenda && session.description) ||
                        !session.type
                      "
                    >
                      <div
                        v-if="!(conferencesId == 3357 && !session.type)"
                        style="padding: 5px 0; font-size: 14px"
                        v-html="truncateDescription(session.description)"
                      ></div>
                    </div>
                  </div>

                  <!-- session sponsor -->
                  <div v-if="session?.sponsor?.photo_url">
                    <img
                      :src="
                        session.sponsor.photo_url
                          ? session.sponsor.photo_url
                          : '/img/noimage-sponsors-multiple.svg'
                      "
                      alt=""
                      style="padding: 5px; max-width: 300px"
                    />
                  </div>
                </div>

                <!-- presenters -->
                <div
                  class="flex-table__session-presenters"
                  v-if="session?.presenters?.length"
                >
                  <template
                    v-for="(presenter, presenterIndex) in session.presenters"
                  >
                    <a
                      href="#"
                      @click.prevent="onOpenPresenterDetailModal(presenter)"
                      :data-id="presenter.id"
                    >
                      {{ presenter.name }}
                    </a><span v-if="presenterIndex + 1 < session.presenters.length">;&nbsp;</span>
                  </template>
                </div>

                <!-- credit hours -->
                <div class="ce-credit-hours-wrapper">
                  <template v-if="+session.credit_hours > 0 && !removeCeInfo">
                    <span>
                      {{
                        conferenceId == 3300
                          ? "CE Contact Hours:"
                          : conferenceId == 2027
                          ? "CPD Credit Hours:"
                          : !session.type
                          ? "Hours:"
                          : "CE Credit Hours:"
                      }}
                    </span>
                    {{ session.credit_hours }}

                    <span class="black" v-if="session.is_virtual_session">
                      &nbsp;&nbsp;&nbsp; Live Interactive Webinar
                    </span>
                  </template>

                  <!-- session format -->
                  <div v-if="session.format">
                    <span>Format:</span>
                    <span class="font-weight-normal">{{ session.format }}</span>
                  </div>

                  <!-- session tracks -->
                  <div v-if="session?.tracks?.length">
                    <span>Track:</span>
                    <span
                      class="font-weight-normal"
                      v-for="(track, trackIndex) in session.tracks"
                      :key="`track-${trackIndex}`"
                      >{{ track
                      }}<span v-if="trackIndex + 1 < session.tracks.length"
                        >,&nbsp;
                      </span></span
                    >
                  </div>

                  <!-- isHybridConference -->
                  <div v-if="isHybridConference">
                    <span v-if="session.is_hybrid_session">
                      <i class="fa-regular fa-laptop-house"></i>&nbsp;&nbsp;
                      Hybrid Session
                    </span>

                    <span v-else>
                      <span v-if="session.is_inperson_session">
                        <i class="fa-regular fa-people-group"></i>&nbsp;&nbsp;
                        <span>In-Person Only</span>
                      </span>

                      <span v-if="session.is_virtual_session">
                        <i class="fa-regular fa-laptop"></i>&nbsp;&nbsp;
                        <span>Virtual Only</span>
                      </span>
                    </span>
                  </div>

                  <!-- event topics -->
                  <template v-if="session?.topics?.length" v-for="topic in session.topics">
                    <div
                      class="event-topics"
                      v-if="topic.hours > 0"
                    >
                      <i class="fa-regular" :class="topic.icon"></i>&nbsp;
                      {{ topic.hours }}&nbsp;<span>{{ topic.title }}</span>
                    </div>
                  </template>
                </div>

                <!-- location -->
                <div class="flex-table__location" v-if="session.location">
                  {{ session.location }}
                </div>

                <!-- room name -->
                <div v-if="session.show_room_name">
                  <strong>Room: </strong> {{ session.room_name }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>

    <div v-if="!agendaSessions?.length && !isFetching" class="flex-table default-shadow">
      <h4>Sessions not found</h4>
    </div>

    <filters-modal
      :filters="agendaFilters"
      :search-filters.sync="searchFilters"
      @search="fetchAgenda"
      @clear="onClearAgendaFilters"
      :is-fetching="isFetching"
    />

    <presenter-detail-modal
      v-show="showPresenterDetailModal"
      :presenter="presenter"
    />

    <session-detail-modal
      v-if="showSessionDetailModal"
      :session="session"
      :slug="slug"
      :is-hybrid-conference="isHybridConference"
    />
  </div>
</template>

<script>
import { debounce } from "../../../global-declare-components/helpers/debounce";
import agendaAPI from "../../api/agendaAPI";
import TimeIcon from "./TimeIcon";
import PresenterDetailModal from "./PresenterDetailModal";
import SessionDetailModal from "./SessionDetailModal";
import FiltersModal from "./FiltersModal";

export default {
  props: {
    slug: {
      type: String,
      default: () => "",
    },
    organizationId: {
      type: Number,
      required: true,
    },
    conferenceId: {
      type: Number,
      required: true,
    },
    isHybridConference: {
      type: Boolean,
      required: true,
    },
    removeCeInfo: {
      type: String,
      default: () => "",
    },
  },
  components: {
    TimeIcon,
    FiltersModal,
    SessionDetailModal,
    PresenterDetailModal,
  },
  mounted() {
    this.fetchAgenda();
  },
  data() {
    return {
      isFetching: false,
      agendaSessions: [],
      showSessionDetailModal: false,
      session: {},
      showPresenterDetailModal: false,
      presenter: {},
      agendaFilters: {
        days: [],
        formats: [],
        rooms: [],
        tracks: [],
        types: [],
        visiting_types: []
      },
      search: '',
      searchFilters: {
        days: '',
        formats: '',
        rooms: '',
        tracks: '',
        types: '',
        visiting_types: ''
      },

      showAgendaFiltersModal: false
    }
  },
  computed: {},
  methods: {
    showModalsForScreenShots(array) {
      const urlParams = new URLSearchParams(window.location.search);
      const presenterId = urlParams.get('presenter_id');
      const sessionId = urlParams.get('session_id');

      if (presenterId || sessionId) {
        const uniqueSessions = new Set();
        const uniquePresenters = new Set();

        array.forEach(day => {
          day.times.forEach(time => {
            time.items.forEach(item => {
              uniqueSessions.add(item);

              item.presenters.forEach(presenter => {
                uniquePresenters.add(presenter);
              });
            });
          });
        });

        const sessionsArray = [...uniqueSessions];
        const presentersArray = [...uniquePresenters];

        if (presenterId) {
          const presenter = presentersArray.find(item => item.id === +presenterId);

          if (presenter) {
            this.onOpenPresenterDetailModal(presenter);
          }
        }
        if (sessionId) {
          const session = sessionsArray.find(item => item.id === +sessionId);

          if (session) {
            this.onOpenSessionDetailModal(session);
          }
        }
      }
    },

    fetchAgenda() {
      const params = this.searchFilters || {};
      params.search = this.search;

      this.isFetching = true;
      agendaAPI
        .fetch(this.slug, params)
        .then((res) => {
          this.agendaSessions = res.data.data || [];
          this.agendaFilters = res.data.filters || {};

          this.showModalsForScreenShots(res.data.data);
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false;
        });
    },

    truncateDescription(description) {
      const maxLength = 500;
      if (description.length > maxLength) {
        return description.slice(0, maxLength) + "...";
      }
      return description;
    },

    onOpenSessionDetailModal(item) {
      setTimeout(() => {
        $("#sessionDetailModalVue").modal({
          show: true,
          backdrop: "static",
          keyboard: false,
        });
      }, 500);

      this.showSessionDetailModal = true;
      this.session = item || {};
    },

    onCloseSessionDetailModal() {
      this.showSessionDetailModal = false;
      this.session = null;
    },

    onOpenPresenterDetailModal(item) {
      setTimeout(() => {
        $("#presenterDetailModalVue").modal({
          show: true,
          backdrop: "static",
          keyboard: false,
        });
      }, 500);

      this.showPresenterDetailModal = true;
      this.presenter = item;
    },

    onClosePresenterDetailModal() {
      this.showPresenterDetailModal = false;
      this.presenter = null;
    },

    onHandleSearch: debounce(function () {
      this.fetchAgenda();
    }, 1000),

    onOpenAgendaFiltersModal() {
      setTimeout(() => {
        $("#agendaFiltersModalVue").modal({
          show: true,
          backdrop: "static",
          keyboard: false,
        });
      }, 500);
    },

    onClearAgendaFilters() {
      this.searchFilters = {
        days: '',
        formats: '',
        rooms: '',
        tracks: '',
        types: '',
        visiting_types: ''
      };

      this.fetchAgenda();
    }
  },
};
</script>

<style lang="scss" scoped>
.filters-wrapper {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  width: 100%;

  .form-group {
    flex-grow: 1;
    margin-bottom: 0;
  }

  .full-width-input {
    width: 100%;
    box-sizing: border-box;
  }
}
</style>
